import type {
  AppointmentDraftEditableResponse,
  AppointmentDraftNotEditableReason,
  AppointmentDraftSaveRequest,
} from "../../../shared/api-types";
import {
  canonicalJson,
  cloneRichTextDocument,
  type RichTextDocument,
} from "../../../shared/rich-text-document";

export const APPOINTMENT_DRAFT_AUTOSAVE_DELAY_MS = 750;

export type AppointmentDraftSaveState =
  | "saved"
  | "saving"
  | "failed"
  | "conflict"
  | "not_editable";
type AppointmentDraftAutosavePhase =
  | "idle"
  | "scheduled"
  | "saving"
  | "failed"
  | "conflict"
  | "not_editable";

export interface AppointmentDraftAutosaveSnapshot {
  document: RichTextDocument;
  persistedDraft: AppointmentDraftEditableResponse;
  status: AppointmentDraftSaveState;
  dirty: boolean;
  conflict: AppointmentDraftEditableResponse | null;
  /**
   * Set when the server answered the terminal not-editable refusal: the Draft
   * left its editable state underneath this tab. Retry would receive the same
   * refusal, so the autosave parks permanently; the RD's local document stays
   * in `document` for the surface to preserve.
   */
  notEditableReason: AppointmentDraftNotEditableReason | null;
}

export type AppointmentDraftSaveOutcome =
  | { status: "saved"; value: AppointmentDraftEditableResponse }
  | { status: "conflict"; current: AppointmentDraftEditableResponse }
  | { status: "not_editable"; reason: AppointmentDraftNotEditableReason };

export type AppointmentDraftHeadObservation =
  | "current"
  | "save_pending"
  | "stale"
  | "unavailable";

export function isMatchingApprovalResponse(
  response: AppointmentDraftEditableResponse,
  requestedRevisionId: string,
  currentRevisionId: string,
  dirty: boolean,
): boolean {
  return !dirty
    && currentRevisionId === requestedRevisionId
    && response.revision.id === requestedRevisionId
    && isRevisionBoundDraftResponse(response)
    && response.review.state === "approved";
}

export function isRevisionBoundDraftResponse(
  response: AppointmentDraftEditableResponse,
): boolean {
  return response.review.state !== "approved"
    || response.review.approval.draftRevisionId === response.revision.id;
}

interface AppointmentDraftAutosaveOptions {
  schedule?: (run: () => void, delay: number) => ReturnType<typeof setTimeout>;
  cancel?: (timer: ReturnType<typeof setTimeout>) => void;
  onError?: (error: unknown) => void;
}

/**
 * Autosave for a completed SOAP Draft revision, mirroring
 * `AppointmentNoteAutosave`: the same four surfaced states (saving, saved,
 * failed, conflict), the same injectable scheduling seams, and the same dirty
 * flag the route dirty-guard reads.
 *
 * The one structural difference is the base revision. A Draft always has a
 * head — the machine-generated Version this edit descends from — so
 * `baseRevisionId` is never null, and every save is a compare-and-set against
 * it. A save answered `conflict` pauses autosave and keeps the RD's text
 * intact until they choose: take the newer revision, or save theirs on top of
 * it. Nothing is discarded without that choice.
 *
 * There is no client-generated save id here. `soap_draft_versions` is
 * append-only with no `save_id` column, so the server recognizes a redelivered
 * save from the committed rows instead — same base, same author, same content
 * digest, exactly one sequence above. Retrying therefore resends the SAME
 * pending request rather than a freshly keyed one, which is what makes that
 * recognition exact.
 */
export class AppointmentDraftAutosave {
  private document: RichTextDocument;
  private savedDocument: RichTextDocument;
  private persistedDraft: AppointmentDraftEditableResponse;
  private persistedDraftSnapshot: AppointmentDraftEditableResponse | null = null;
  private baseRevisionId: string;
  private phase: AppointmentDraftAutosavePhase = "idle";
  private conflict: AppointmentDraftEditableResponse | null = null;
  private notEditableReason: AppointmentDraftNotEditableReason | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pendingRequest: AppointmentDraftSaveRequest | null = null;
  private disposed = false;
  private readonly schedule: NonNullable<AppointmentDraftAutosaveOptions["schedule"]>;
  private readonly cancel: NonNullable<AppointmentDraftAutosaveOptions["cancel"]>;
  private readonly onError: (error: unknown) => void;

  constructor(
    initial: AppointmentDraftEditableResponse,
    private readonly save: (
      request: AppointmentDraftSaveRequest,
    ) => Promise<AppointmentDraftSaveOutcome>,
    private readonly changed: (snapshot: AppointmentDraftAutosaveSnapshot) => void,
    options: AppointmentDraftAutosaveOptions = {},
  ) {
    this.document = cloneRichTextDocument(initial.document);
    this.savedDocument = cloneRichTextDocument(this.document);
    this.persistedDraft = clonePersistedDraft(initial);
    this.baseRevisionId = initial.revision.id;
    const schedule = options.schedule;
    const cancel = options.cancel;
    this.schedule = schedule
      ? (run, delay) => schedule(run, delay)
      : (run, delay) => globalThis.setTimeout(run, delay);
    this.cancel = cancel
      ? (timer) => cancel(timer)
      : (timer) => globalThis.clearTimeout(timer);
    this.onError = options.onError ?? (() => {});
  }

  snapshot(): AppointmentDraftAutosaveSnapshot {
    this.persistedDraftSnapshot ??= clonePersistedDraft(this.persistedDraft);
    return {
      document: cloneRichTextDocument(this.document),
      persistedDraft: this.persistedDraftSnapshot,
      status: this.saveState(),
      dirty: this.hasUnsavedWork(),
      conflict: this.conflict,
      notEditableReason: this.notEditableReason,
    };
  }

  edit(document: RichTextDocument): void {
    if (this.disposed) return;
    this.document = cloneRichTextDocument(document);
    if (
      this.phase === "conflict"
      || this.phase === "saving"
      || this.phase === "failed"
      || this.phase === "not_editable"
    ) {
      this.emit();
      return;
    }
    this.queueAutosave();
  }

  retry(): void {
    if (this.disposed || this.phase !== "failed") return;
    void this.flush(true);
  }

  /** Classifies a copy-time head read and adopts a confirmed external head. */
  observeServerHead(current: AppointmentDraftEditableResponse): AppointmentDraftHeadObservation {
    if (this.disposed) return "unavailable";
    if (!isRevisionBoundDraftResponse(current)) return "unavailable";
    if (this.phase === "saving" && this.pendingRequest !== null) return "save_pending";
    if (current.revision.sequence < this.persistedDraft.revision.sequence) {
      return "unavailable";
    }
    if (
      current.revision.sequence === this.persistedDraft.revision.sequence
      && current.revision.id !== this.persistedDraft.revision.id
    ) return "unavailable";
    if (current.revision.id === this.persistedDraft.revision.id) {
      this.replacePersistedDraft(current);
      this.emit();
      return "current";
    }
    if (this.timer) this.cancel(this.timer);
    this.timer = null;
    this.pendingRequest = null;
    this.conflict = clonePersistedDraft(current);
    this.replacePersistedDraft(current);
    this.phase = "conflict";
    this.emit();
    return "stale";
  }

  /** Adopts the newer revision, discarding this tab's unsaved text. */
  useLatest(): RichTextDocument | null {
    if (this.disposed || !this.conflict) return null;
    this.document = cloneRichTextDocument(this.conflict.document);
    this.savedDocument = cloneRichTextDocument(this.document);
    this.baseRevisionId = this.conflict.revision.id;
    this.pendingRequest = null;
    this.conflict = null;
    this.phase = "idle";
    this.emit();
    return cloneRichTextDocument(this.document);
  }

  /** Rebases this tab's text onto the newer revision and saves it on top. */
  saveMine(): void {
    if (this.disposed || !this.conflict) return;
    this.baseRevisionId = this.conflict.revision.id;
    this.pendingRequest = this.createRequest();
    this.conflict = null;
    this.phase = "idle";
    void this.flush(true);
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer) this.cancel(this.timer);
    this.timer = null;
  }

  private queueAutosave(): void {
    if (this.disposed) return;
    if (this.timer) this.cancel(this.timer);
    if (!this.isDirty()) {
      this.timer = null;
      this.phase = "idle";
      this.emit();
      return;
    }
    this.phase = "scheduled";
    this.timer = this.schedule(() => {
      this.timer = null;
      void this.flush();
    }, APPOINTMENT_DRAFT_AUTOSAVE_DELAY_MS);
    this.emit();
  }

  private async flush(force = false): Promise<void> {
    if (
      this.disposed
      || this.phase === "saving"
      || this.phase === "conflict"
      || this.phase === "not_editable"
    ) return;
    if (!this.hasUnsavedWork()) return;
    if (!force && this.phase === "failed") return;
    this.phase = "saving";
    this.emit();

    while (!this.disposed) {
      this.pendingRequest ??= this.createRequest();
      let outcome: AppointmentDraftSaveOutcome;
      try {
        outcome = await this.save(this.pendingRequest);
      } catch (error) {
        if (this.disposed) return;
        this.onError(error);
        this.phase = "failed";
        this.emit();
        return;
      }
      if (this.disposed) return;
      if (outcome.status === "not_editable") {
        // Terminal: the Draft left its editable state underneath this tab.
        // Retrying would receive the same refusal, so the autosave parks and
        // keeps the RD's local document for the surface to preserve.
        this.pendingRequest = null;
        this.phase = "not_editable";
        this.notEditableReason = outcome.reason;
        this.emit();
        return;
      }
      if (outcome.status === "conflict") {
        this.pendingRequest = null;
        this.phase = "conflict";
        this.conflict = outcome.current;
        this.replacePersistedDraft(outcome.current);
        this.emit();
        return;
      }
      this.savedDocument = cloneRichTextDocument(outcome.value.document);
      this.replacePersistedDraft(outcome.value);
      this.baseRevisionId = outcome.value.revision.id;
      this.pendingRequest = null;
      if (!this.isDirty()) {
        this.phase = "idle";
        this.emit();
        return;
      }
      // Publish this accepted head before a chained save can replace it.
      this.emit();
    }
  }

  private createRequest(): AppointmentDraftSaveRequest {
    return {
      document: cloneRichTextDocument(this.document),
      baseRevisionId: this.baseRevisionId,
    };
  }

  private isDirty(): boolean {
    return canonicalJson(this.document) !== canonicalJson(this.savedDocument);
  }

  private hasUnsavedWork(): boolean {
    return this.pendingRequest !== null || this.isDirty();
  }

  private saveState(): AppointmentDraftSaveState {
    switch (this.phase) {
      case "idle": return "saved";
      case "scheduled":
      case "saving": return "saving";
      case "failed": return "failed";
      case "conflict": return "conflict";
      case "not_editable": return "not_editable";
    }
  }

  private replacePersistedDraft(draft: AppointmentDraftEditableResponse): void {
    this.persistedDraft = clonePersistedDraft(draft);
    this.persistedDraftSnapshot = null;
  }

  private emit(): void {
    if (this.disposed) return;
    this.changed(this.snapshot());
  }
}

function clonePersistedDraft(
  draft: AppointmentDraftEditableResponse,
): AppointmentDraftEditableResponse {
  return {
    status: "editable",
    document: cloneRichTextDocument(draft.document),
    revision: { ...draft.revision },
    review: draft.review.state === "approved"
      ? { state: "approved", approval: { ...draft.review.approval } }
      : { ...draft.review },
    provenance: { ...draft.provenance },
  };
}
